import { useFolderData } from "@/data/use-folder-data";
import { useKitFoldersData } from "@/data/use-kit-folders-data";
import { KitFolderDto } from "@/lib/gen-api";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

type MoveFilesProps = {
  groupUuid: string;
  kitUuid: string;
  fromFolderUuid: string;
  selectedFileUuids: Set<string>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMoveFiles: () => void;
};

export const MoveFilesDialog: React.FC<MoveFilesProps> = ({
  groupUuid,
  kitUuid,
  selectedFileUuids,
  fromFolderUuid,
  open,
  onOpenChange,
  onMoveFiles,
}) => {
  const { folder, updateFile } = useFolderData(
    groupUuid,
    kitUuid,
    fromFolderUuid,
  );
  const { folders } = useKitFoldersData(groupUuid, kitUuid);
  const files = useMemo(() => {
    return (
      folder?.files.filter((file) => selectedFileUuids.has(file.uuid)) || []
    );
  }, [folder, selectedFileUuids]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);

  const handleMoveFiles = async () => {
    if (!selectedFolder) return;
    for (const file of files) {
      await updateFile({
        fileUuid: file.uuid,
        body: {
          folderUuid: selectedFolder,
        },
      });
    }
    toast.success(
      `Moved ${files.length} file${files.length !== 1 ? "s" : ""} to ${folder?.files.find((f) => f.uuid === selectedFolder)?.name}.`,
    );
    onOpenChange(false);
    onMoveFiles();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move Files</DialogTitle>
          <DialogDescription>
            Moving {files.length} file{files.length !== 1 ? "s" : ""}:{" "}
            {files.map((f) => f.name).join(", ")}
          </DialogDescription>
        </DialogHeader>
        <ul className="flex max-h-[50vh-200px] flex-col gap-2 overflow-y-auto">
          {Object.values<KitFolderDto>(folders).map((folder) => (
            <li key={folder.uuid} className="flex flex-row items-center gap-2">
              <Checkbox
                disabled={folder.uuid === fromFolderUuid}
                checked={
                  selectedFolder === folder.uuid ||
                  folder.uuid === fromFolderUuid
                }
                onCheckedChange={(checked) => {
                  if (checked) {
                    setSelectedFolder(folder.uuid);
                  } else {
                    setSelectedFolder(null);
                  }
                }}
              />
              <div
                className="cursor-default"
                onClick={() => {
                  setSelectedFolder(folder.uuid);
                }}
              >
                {folder.name}
              </div>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <DialogClose asChild>
            <Button size="sm" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button
            size="sm"
            disabled={!selectedFolder}
            onClick={handleMoveFiles}
          >
            Move Files
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
